"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Site Assessment",
  description: "We begin with a comprehensive survey of the raw ground, identifying structural requirements and existing utility locations to ensure a solid foundation for electrical infrastructure.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/site_assessment_raw_ground.png"
}, {
  title: "Infrastructure Planning",
  description: "Our engineers design technical blueprints for temporary power supplies and distribution boards, mapping out the most efficient and safe routing for industrial wiring.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/infrastructure_planning_blueprints.png"
}, {
  title: "Precision Installation",
  description: "Our specialist teams execute the installation of containment systems and cabling with surgical precision, adhering to the highest industrial safety standards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/electrical_installation_precision.png"
}, {
  title: "System Power-On",
  description: "Final testing and energisation of the site infrastructure. We verify every circuit and distribution point, delivering a fully operational site ready for the next phase.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/power_on_energized_site.png"
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="223854" ref={ref} data-index={index} className={cn("relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 items-center mb-20 md:mb-32 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="absolute left-4 md:left-1/2 top-16 md:top-1/2 w-4 h-4 rounded-none bg-primary -translate-x-1/2 md:-translate-y-1/2 z-10 ring-4 ring-background shadow-[0_0_15px_rgba(251,191,36,0.5)]" />

      {}
      <div className={cn("pl-12 md:pl-0 flex justify-start", isEven ? "md:justify-end md:pr-16" : "md:justify-start md:pl-16 md:col-start-2 md:row-start-1")}>
        <img src={step.image} alt={step.title} className="w-32 h-32 md:w-80 md:h-80 rounded-none object-cover shadow-2xl border-2 border-border grayscale hover:grayscale-0 transition-all duration-500" />
      </div>

      {}
      <div className={cn("pl-12 md:pl-0", isEven ? "md:pl-16" : "md:pr-16 md:col-start-1 md:row-start-1")}>
        <Card className="border border-border/50 shadow-none bg-card/40 backdrop-blur-md hover:border-primary/50 transition-colors duration-300 rounded-none">
          <CardHeader>
            <div className="text-sm font-mono font-bold text-primary mb-2 tracking-widest uppercase">
              Phase 0{index + 1}
            </div>
            <CardTitle className="text-2xl md:text-3xl font-default font-bold text-foreground">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg font-default">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function QualityJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="our-site-readiness-process" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Our Site Readiness Process
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            A systematic approach to electrical infrastructure. We transform raw sites into fully powered environments through technical precision and industrial expertise.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px border-l-2 border-dashed border-primary/30 md:-translate-x-1/2" />

          {}
          <div className="relative z-10 pt-8">
            {steps.map((step, index) => <TimelineItem key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}